import { flatten as _flatten, forEach as _forEach, groupBy as _groupBy } from 'lodash-es';

import { IGeocodeInfo } from './address-has-good-geocode';
import { EntityMarkerGEOJsonType } from './EntityMarkerGEOJsonType';
import { extractAllUnlessConfirmedAddresses } from './extract-all-unless-confirmed-addresses';
import { extractBestAddresses } from './extract-best-addresses';
import { IEntityMarkerFeatureFactory } from './IEntityMarkerFeatureFactory';
import { pointsAsFeatureCollection } from './points-to-feature-collection';

export class EntityGeoJSONFeatureFactory<TEntityInput> {

  /**
   *
   */
  constructor(
    private _entityMarkerFeatureFactory: IEntityMarkerFeatureFactory<TEntityInput>
  ) {
  }

  /** Pass an address extractor function */
  createLayer(entities: TEntityInput[],
    addressExtractorFn: <T extends IGeocodeInfo>(addressInput: Array<T>) => T[],
    optData?: Record<string, any>) {

      const points = (entities || []).map((entity) => this._entityMarkerFeatureFactory.buildMarkerFeatures(
      entity,
      addressExtractorFn,
      optData
    ));

    const mergedPoints = this.mergeMarkerFeatures(
      _flatten(points)
    );

    return pointsAsFeatureCollection(mergedPoints);
  }

  /**
   * Creates a geojson layer which include only the best addresses
   *
   * @param entities Entties to create layers for
   */
  createBestAddressLayer(entities: TEntityInput[], optData?: Record<string, any>) {
    const points = (entities || []).map((entity) => this._entityMarkerFeatureFactory.buildMarkerFeatures(
      entity,
      extractBestAddresses,
      optData
    ));

    const mergedPoints = this.mergeMarkerFeatures(
      _flatten(points)
    );

    return pointsAsFeatureCollection(mergedPoints);
  }

  /**
   * Creats a layer of all "good" addresses for an enttity
   *
   * @param entities Entities for layers
   */
  createAllAddressesLayer(entities: Array<TEntityInput>, optData?: Record<string, any>) {
    const unMergedMarkers = (entities || []).map((entity) => this._entityMarkerFeatureFactory.buildMarkerFeatures(
      entity,
      extractAllUnlessConfirmedAddresses,
      optData
    ));

    const mergedFeatures = this.mergeMarkerFeatures(_flatten(unMergedMarkers));

    return pointsAsFeatureCollection(mergedFeatures);
  }

  // When multiple people are at the same coordinates, we merge
  // the feature into a single record
  mergeMarkerFeatures(pointFeatures: EntityMarkerGEOJsonType[]) {
    const result: EntityMarkerGEOJsonType[] = [];

    // Group by coordinates
    const groupings: { [K: string]: EntityMarkerGEOJsonType[] } = _groupBy(
      pointFeatures,
      (point: EntityMarkerGEOJsonType) => point.geometry.coordinates.toString()
    );

    _forEach(groupings, (points: EntityMarkerGEOJsonType[]) => {
      // Only one item, push it
      if (points.length === 1) {
        result.push(points[0]);
      } else {
        // More than 1 item on the same coordinate
        // Do some logic here to do merge them into a single point
        const mergedFeature = this._entityMarkerFeatureFactory.createdMergedPoint(points);
        result.push(mergedFeature);
      }
    });

    return result;
  }

}
